import { useCallback, useEffect, useState } from "react";
import { fetchHospitalProfile, updateHospitalProfile } from "../api/hospitalApi";
import { hospitalUserProfile } from "../data";
import { getAuthToken } from "../auth";

export function useHospitalProviderProfile() {
  const [name, setName] = useState(hospitalUserProfile.companyName);
  const [address, setAddress] = useState(hospitalUserProfile.address);
  const [phone, setPhone] = useState(hospitalUserProfile.phone);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setName(hospitalUserProfile.companyName);
      setAddress(hospitalUserProfile.address);
      setPhone(hospitalUserProfile.phone);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const profile = await fetchHospitalProfile();
      setName(profile.name);
      setAddress(profile.address ?? "");
      setPhone(profile.phone ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger le profil.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Le nom du prestataire est obligatoire.");
      return false;
    }

    if (!getAuthToken()) {
      setError(null);
      return true;
    }

    setSaving(true);
    setError(null);
    setUsingApi(true);

    try {
      const profile = await updateHospitalProfile({
        name: trimmedName,
        address: address.trim(),
        phone: phone.trim(),
      });
      setName(profile.name);
      setAddress(profile.address ?? "");
      setPhone(profile.phone ?? "");
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible d'enregistrer le profil.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [name, address, phone]);

  return {
    name,
    address,
    phone,
    loading,
    saving,
    error,
    usingApi,
    setName,
    setAddress,
    setPhone,
    save,
    reload: load,
  };
}
